import { create } from 'zustand';
import dayjs from 'dayjs';
import { EAppointmentType, TAppointmentDateTimeSlot } from '../types';
import { DateOption, defaultAppointmentModalState, TimeOption } from '../utils';

interface AppointmentModalState {
  isOpen: boolean;
  step: number;
  projectId?: number;
  dateTimeSlot?: TAppointmentDateTimeSlot;
  name: string;
  email: string;
  phone: string;
  appointmentType: EAppointmentType;
  duration: number;
  notes: string;
  selectedDate?: Date;
  selectedTime?: TimeOption;

  dateOptions: DateOption[];
  timeOptions: TimeOption[];
  slotsByDate: Map<string, TAppointmentDateTimeSlot[]>;

  actions: {
    setIsOpen: (isOpen: boolean) => void;
    setStep: (step: number) => void;
    setProjectId: (projectId: number) => void;
    setDateTimeSlot: (dateTimeSlot: TAppointmentDateTimeSlot) => void;
    setName: (name: string) => void;
    setEmail: (email: string) => void;
    setPhone: (phone: string) => void;
    setAppointmentType: (appointmentType: EAppointmentType) => void;
    setDuration: (duration: number) => void;
    setNotes: (notes: string) => void;
    setSelectedDate: (selectedDate: Date) => void;
    setSelectedTime: (selectedTime: TimeOption | undefined) => void;

    resetStore: () => void;
    updateStore: (store: Partial<AppointmentModalState>) => void;

    setDateOptions: (dateOptions: DateOption[]) => void;
    setTimeOptions: (timeOptions: TimeOption[]) => void;
    setSlotsByDate: (slotsByDate: Map<string, TAppointmentDateTimeSlot[]>) => void;

    setFirstDate: (dateOptions: DateOption[]) => void;
    getTimeSlotsForDate: (
      selectedDate: Date,
      slotsByDateExternal?: Map<string, TAppointmentDateTimeSlot[]>,
    ) => TimeOption[];
  };
}

export const useCalendarStore = create<AppointmentModalState>((set, get) => {
  return {
    isOpen: false,
    step: 1,
    projectId: undefined,
    dateTimeSlot: undefined,
    appointmentType: defaultAppointmentModalState.appointmentType,
    duration: defaultAppointmentModalState.duration,
    notes: defaultAppointmentModalState.notes,
    name: defaultAppointmentModalState.name,
    email: defaultAppointmentModalState.email,
    phone: defaultAppointmentModalState.phone,
    selectedDate: undefined,
    selectedTime: undefined,

    dateOptions: [],
    timeOptions: [],
    slotsByDate: new Map(),

    actions: {
      setIsOpen: (isOpen) => {
        set({ isOpen });
      },
      setStep: (step) => {
        set({ step });
      },
      setProjectId: (projectId) => {
        set({ projectId });
      },
      setDateTimeSlot: (dateTimeSlot) => {
        set({ dateTimeSlot });
      },
      setName: (name) => {
        set({ name });
      },
      setEmail: (email) => {
        set({ email });
      },
      setPhone: (phone) => {
        set({ phone });
      },
      setAppointmentType: (appointmentType) => {
        set({ appointmentType });
      },
      setDuration: (duration) => {
        set({ duration });
      },
      setNotes: (notes) => {
        set({ notes });
      },
      setSelectedDate: (selectedDate) => {
        set({ selectedDate });
      },
      setSelectedTime: (selectedTime) => {
        set({ selectedTime });
      },

      resetStore: () => {
        set(defaultAppointmentModalState);
      },
      updateStore: (store) => {
        set((state) => ({ ...state, ...store }));
      },
      setDateOptions: (dateOptions) => {
        set({ dateOptions });
      },
      setTimeOptions: (timeOptions) => {
        set({ timeOptions });
      },
      setSlotsByDate: (slotsByDate) => {
        set({ slotsByDate });
      },

      setFirstDate: (dateOptions) => {
        const state = get();
        if (dateOptions.length > 0) {
          const firstDate = dateOptions[0].date;
          const timeSlots = state.actions.getTimeSlotsForDate(firstDate);
          set({
            selectedDate: firstDate,
            selectedTime: timeSlots.length > 0 ? timeSlots[0] : undefined,
            timeOptions: timeSlots,
          });
        }
      },

      getTimeSlotsForDate: (selectedDate, slotsByDateExternal) => {
        const slotsByDate = slotsByDateExternal || get().slotsByDate;
        const dateStr = dayjs(selectedDate).format('YYYY-MM-DD');
        const slots = slotsByDate.get(dateStr) || [];

        return slots
          .sort((a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf()) // Sort by time
          .map((slot) => ({
            time: dayjs(slot.start).format('HH:mm'), // 24-hour format
            start: slot.start,
            end: slot.end,
          }));
      },
    },
  };
});

export const useIsOpen = () => useCalendarStore((state) => state.isOpen);
export const useStep = () => useCalendarStore((state) => state.step);
export const useProjectId = () => useCalendarStore((state) => state.projectId);
export const useDateTimeSlot = () => useCalendarStore((state) => state.dateTimeSlot);
export const useName = () => useCalendarStore((state) => state.name);
export const useEmail = () => useCalendarStore((state) => state.email);
export const usePhone = () => useCalendarStore((state) => state.phone);
export const useAppointmentType = () => useCalendarStore((state) => state.appointmentType);
export const useDuration = () => useCalendarStore((state) => state.duration);
export const useNotes = () => useCalendarStore((state) => state.notes);
export const useSelectedDate = () => useCalendarStore((state) => state.selectedDate);
export const useSelectedTime = () => useCalendarStore((state) => state.selectedTime);

export const useDateOptions = () => useCalendarStore((state) => state.dateOptions);
export const useSlotsByDate = () => useCalendarStore((state) => state.slotsByDate);

export const useAppointmentModalActions = () => useCalendarStore((state) => state.actions);
export const useAppointmentModalState = () => useCalendarStore((state) => state);
